import {
  DEFAULT_PROACTIVE_GREETING_REENGAGE_SECONDS,
  type ProactiveGreetingPage,
} from '@teo/widget-shared';
import { buildWidgetPage } from '../page-context/build-widget-page.js';
import { getWidgetSession } from '../session/widget-session.js';

const STORAGE_PREFIX = 'teo_dialog_page:';
const PENDING_THREAD_KEY = 'pending';

export type DialogPageStickyState = {
  /** Normalized thread key (`pending` until backend assigns an id). */
  threadKey: string;
  lastUserSendAt: number;
  pageAttached: boolean;
};

export type DialogPageSessionOptions = {
  slug?: string;
  threadId?: string | number | null;
  /** Idle gap in ms. Default: re-engage seconds × 1000. */
  idleMs?: number;
  now?: number;
};

/** In-memory mirror so same-tick reads work before storage settles. */
const memoryBySlug = new Map<string, DialogPageStickyState>();

export function currentSendPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

export function normalizeDialogThreadKey(
  threadId?: string | number | null,
): string {
  if (threadId == null) return PENDING_THREAD_KEY;
  const n = Number(threadId);
  if (Number.isFinite(n) && n > 0) return String(n);
  const raw = String(threadId).trim();
  return raw || PENDING_THREAD_KEY;
}

export function resolveDialogPageIdleMs(idleMs?: number): number {
  if (typeof idleMs === 'number' && Number.isFinite(idleMs) && idleMs >= 0) {
    return idleMs;
  }
  return DEFAULT_PROACTIVE_GREETING_REENGAGE_SECONDS * 1000;
}

function resolveSlug(explicit?: string): string | null {
  const trimmed = explicit?.trim();
  if (trimmed) return trimmed;
  return getWidgetSession()?.slug?.trim() || null;
}

function storageKey(slug: string): string {
  return `${STORAGE_PREFIX}${slug}`;
}

function readSticky(slug: string): DialogPageStickyState | null {
  const fromMemory = memoryBySlug.get(slug);
  if (fromMemory) return fromMemory;

  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DialogPageStickyState;
    if (
      !parsed ||
      typeof parsed.threadKey !== 'string' ||
      typeof parsed.lastUserSendAt !== 'number' ||
      typeof parsed.pageAttached !== 'boolean'
    ) {
      return null;
    }
    memoryBySlug.set(slug, parsed);
    return parsed;
  } catch {
    return null;
  }
}

function writeSticky(slug: string, state: DialogPageStickyState): void {
  memoryBySlug.set(slug, state);
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(state));
  } catch {
    // Quota / private mode — memory still works for this page load.
  }
}

function clearSticky(slug: string): void {
  memoryBySlug.delete(slug);
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(slug));
  } catch {
    // ignore
  }
}

/**
 * Active dialog session: page already attached, same thread (or still pending),
 * and last user send within the idle window. Navigation does not end the session.
 */
export function isActiveDialogPageSession(
  options: DialogPageSessionOptions = {},
): boolean {
  const slug = resolveSlug(options.slug);
  if (!slug) return false;

  const sticky = readSticky(slug);
  if (!sticky?.pageAttached) return false;

  const now = options.now ?? Date.now();
  const idleMs = resolveDialogPageIdleMs(options.idleMs);
  if (now - sticky.lastUserSendAt >= idleMs) return false;

  const currentKey = normalizeDialogThreadKey(options.threadId);
  if (sticky.threadKey === currentKey) return true;
  // First-dialog sticky still pending while thread id arrives mid-session.
  if (sticky.threadKey === PENDING_THREAD_KEY) return true;
  return false;
}

/**
 * Whether the next user send should include greeting-shaped `page`.
 * True when there is no active dialog page session (idle / new thread / fresh).
 */
export function shouldAttachPageToSend(
  options: DialogPageSessionOptions | string = {},
): boolean {
  // Legacy signature: shouldAttachPageToSend(pathname) — pathname ignored.
  if (typeof options === 'string') {
    return !isActiveDialogPageSession();
  }
  return !isActiveDialogPageSession(options);
}

/** Mark that `page` was attached for this dialog session. */
export function markPageAttachedToSend(
  options: DialogPageSessionOptions | string = {},
): void {
  // Legacy: markPageAttachedToSend(pathname)
  const opts: DialogPageSessionOptions =
    typeof options === 'string' ? {} : options;
  const slug = resolveSlug(opts.slug);
  if (!slug) return;

  const now = opts.now ?? Date.now();
  writeSticky(slug, {
    threadKey: normalizeDialogThreadKey(opts.threadId),
    lastUserSendAt: now,
    pageAttached: true,
  });
}

/**
 * Refresh activity clock (and promote pending → real thread id) on every
 * user send, including sends that omit `page`.
 */
export function touchDialogPageActivity(
  options: DialogPageSessionOptions = {},
): void {
  const slug = resolveSlug(options.slug);
  if (!slug) return;

  const now = options.now ?? Date.now();
  const currentKey = normalizeDialogThreadKey(options.threadId);
  const sticky = readSticky(slug);

  let threadKey = sticky?.threadKey ?? currentKey;
  // Promote pending → real id; switch when backend rotates the thread.
  if (currentKey !== PENDING_THREAD_KEY) {
    if (!sticky || sticky.threadKey === PENDING_THREAD_KEY) {
      threadKey = currentKey;
    } else if (sticky.threadKey !== currentKey) {
      threadKey = currentKey;
    }
  }

  writeSticky(slug, {
    threadKey,
    lastUserSendAt: now,
    pageAttached: sticky?.pageAttached ?? false,
  });
}

/**
 * Allow the next send to attach `page` again (failed send without queue).
 * Pathname argument is ignored (legacy call sites).
 */
export function releasePageAttachmentForSend(
  _pathname?: string,
  options?: DialogPageSessionOptions,
): void {
  const slug = resolveSlug(options?.slug);
  if (!slug) return;
  const sticky = readSticky(slug);
  if (!sticky) return;
  writeSticky(slug, { ...sticky, pageAttached: false });
}

/** Clear dialog page sticky (new-chat test / explicit reset). */
export function resetPageAttachmentForSend(slug?: string): void {
  if (slug?.trim()) {
    clearSticky(slug.trim());
    return;
  }
  const sessionSlug = getWidgetSession()?.slug;
  if (sessionSlug) clearSticky(sessionSlug);
  // Also wipe any leftover memory keys (tests / multi-slug).
  for (const key of [...memoryBySlug.keys()]) {
    clearSticky(key);
  }
}

/**
 * Dialog-session `page` for `POST /api/widget/send`.
 * Attaches once per active chat idle window; navigation does not re-attach.
 * After idle (≥ re-engage seconds) or thread change — attaches current page again.
 */
export async function takePageForSend(options?: {
  url?: string;
  pageContextEnabled?: boolean;
  slug?: string;
  threadId?: string | number | null;
  idleMs?: number;
  now?: number;
}): Promise<ProactiveGreetingPage | undefined> {
  const sessionOpts: DialogPageSessionOptions = {
    slug: options?.slug,
    threadId: options?.threadId,
    idleMs: options?.idleMs,
    now: options?.now,
  };

  const slug = resolveSlug(sessionOpts.slug);
  const pathname = currentSendPathname();

  if (slug && isActiveDialogPageSession(sessionOpts)) {
    touchDialogPageActivity(sessionOpts);
    return undefined;
  }

  if (slug) {
    markPageAttachedToSend(sessionOpts);
  }

  try {
    return await buildWidgetPage({
      url: options?.url,
      pageContextEnabled: options?.pageContextEnabled,
    });
  } catch {
    try {
      return await buildWidgetPage({
        url: options?.url,
        pageContextEnabled: false,
      });
    } catch {
      const href =
        options?.url ??
        (typeof window !== 'undefined'
          ? window.location.href
          : 'https://localhost/');
      return {
        type: 'other',
        url: href,
        pathname,
      };
    }
  }
}
